/**
 * JOTestimonials — Loads real client reviews from Google (via /api/google-reviews)
 * with a fallback to settings.client_testimonials (the seeded real reviews).
 */
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useSettings } from "@/lib/useSettings";
import { api } from "@/lib/api";

export default function JOTestimonials() {
  const { settings: s } = useSettings();
  const [google, setGoogle] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/google-reviews").then((r) => {
      if (!alive) return;
      const list = (r?.data?.reviews || []).filter((rv) => (rv.text || "").trim().length > 30);
      setGoogle({ reviews: list, meta: r?.data?.meta || {} });
    }).catch(() => {
      if (alive) setGoogle({ reviews: [], meta: {} });
    });
    return () => { alive = false; };
  }, []);

  // Prefer real Google reviews, fallback to seeded testimonials
  const googleReviews = google?.reviews || [];
  const seeded = s?.client_testimonials || [];
  const useGoogle = googleReviews.length >= 3;
  const reviews = useGoogle ? googleReviews : seeded;
  if (!reviews.length) return null;

  // Meta info: prefer Google live numbers
  const avg = useGoogle && google?.meta?.rating ? google.meta.rating : (google?.meta?.avg || s?.reviews_average || 4.9);
  // Use the *fetched* count (what we actually display) rather than the global
  // Google total — keeps the number honest with the testimonials shown.
  const count = useGoogle
    ? (google?.meta?.fetched_count || googleReviews.length)
    : (s?.reviews_count || 127);
  const sourceLabel = useGoogle ? "avis Google vérifiés" : "avis clients";

  return (
    <section className="jo-section jo-section--dark" data-testid="jo-testimonials">
      <div className="jo-container">
        <div className="max-w-2xl mb-14">
          <div className="jo-section-eyebrow">— Le mot de nos clients</div>
          <h2 className="jo-section-title">
            Plus de 30 ans à cultiver<br />la confiance d'Istres.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.slice(0, 6).map((r, i) => {
            const name = r.author || r.author_name || r.name || "Client vérifié";
            const text = r.text || r.snippet || "";
            const rating = r.rating || 5;
            const id = r.id || r.review_id || i;
            return (
              <div key={id} className="jo-testimonial-card" data-testid={`jo-tst-${id}`}>
                <div className="jo-testimonial-quote">"</div>
                <div className="jo-testimonial-text">{text}</div>
                <div className="jo-testimonial-name">{name}</div>
                <div className="jo-testimonial-stars flex gap-0.5">
                  {Array.from({ length: rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-center gap-6 text-sm text-[var(--jdo-cream)]/70">
          <span className="flex items-center gap-2">
            <span className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[var(--jdo-gold)] text-[var(--jdo-gold)]" />)}</span>
            <span><strong className="text-[var(--jdo-cream)]">{avg}/5</strong> · {count} {sourceLabel}</span>
          </span>
        </div>
      </div>
    </section>
  );
}
